import React, { useEffect, useState, useCallback } from 'react';
import axios from 'axios';
import { auth, fmtAmt, toPyeong, lbl, inp, sel, card, AreaRangeInput } from '../RealTradePage';

// ─────────────────────────────────────────────
// 거래목록 탭 - /api/real-trades/list
//  기본 리스트표 + 필터 + 정렬 + 페이징
// ─────────────────────────────────────────────

const BUCKETS = [
  { v: '',  t: '전체 평형' },
  { v: '1', t: '~40㎡ (12평↓)' },
  { v: '2', t: '40~60㎡ (12~18평)' },
  { v: '3', t: '60~85㎡ (18~26평)' },
  { v: '4', t: '85~102㎡ (26~31평)' },
  { v: '5', t: '102~135㎡ (31~41평)' },
  { v: '6', t: '135㎡~ (41평↑)' },
];

const SORTS = [
  { v: 'date_desc',   t: '최근 거래순' },
  { v: 'amount_desc', t: '금액 높은순' },
  { v: 'amount_asc',  t: '금액 낮은순' },
  { v: 'unit_desc',   t: '평당가 높은순' },
  { v: 'area_desc',   t: '면적 넓은순' },
];

export default function TradeList({ lawdCd, from, to, propType, zone}) {
  const [rows, setRows]   = useState([]);
  const [total, setTotal] = useState(0);
  const [pages, setPages] = useState(0);
  const [page, setPage]   = useState(1);
  const [loading, setLoading] = useState(false);

  // 이 탭 전용 필터
  const [aptNm, setAptNm]   = useState('');
  const [bucket, setBucket] = useState('');
  const [arMin, setArMin] = useState('');
  const [arMax, setArMax] = useState('');
  const [sort, setSort]     = useState('date_desc');
  const [umd, setUmd]       = useState('');
  const [umds, setUmds]     = useState([]);

  // 선택한 구의 법정동 목록 로드
  useEffect(() => {
    // 권역 조회 시에는 동 목록을 제공하지 않는다
    if (!lawdCd) { setUmds([]); setUmd(''); return; }
    axios.get(`/api/real-trades/regions?lawd_cd=${lawdCd}&prop_type=${propType}`, auth())
      .then(r => setUmds(r.data.umds || []))
      .catch(() => setUmds([]));
    setUmd('');
  }, [lawdCd, propType]);

  // 목록 조회 - useCallback 으로 감싸 불필요한 재생성 방지
  const load = useCallback(async (p = 1) => {
    if (!lawdCd && !zone) return;
    setLoading(true);
    try {
      const q = new URLSearchParams({ from, to, page: p, size: 50, sort, prop_type: propType });
      if (lawdCd) q.set('lawd_cd', lawdCd);
      else if (zone) q.set('zone', zone);
      if (aptNm)  q.set('apt_nm', aptNm);
      if (arMin) q.set('ar_min', arMin);
      if (arMax) q.set('ar_max', arMax);
      if (umd)    q.set('umd_nm', umd);

      const res = await axios.get(`/api/real-trades/list?${q}`, auth());
      setRows(res.data.rows || []);
      setTotal(res.data.total || 0);
      setPages(res.data.pages || 0);
      setPage(p);
    } catch (e) {
      alert('조회 실패: ' + (e.response?.data?.error || e.message));
    } finally {
      setLoading(false);
    }
  }, [lawdCd, zone, from, to, sort, aptNm, arMin, arMax, umd, propType]);

  useEffect(() => { load(1); }, [load]);

  return (
    <div>
      {/* ── 상세 필터 ── */}
      <div style={{ ...card, display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'center' }}>
        <label style={lbl}>법정동</label>
        <select value={umd} onChange={e => setUmd(e.target.value)} style={sel}>
          <option value="">전체</option>
          {umds.map(u => (
            <option key={u.umd_nm} value={u.umd_nm}>{u.umd_nm} ({u.cnt})</option>
          ))}
        </select>

        <label style={lbl}>단지명</label>
        <input value={aptNm} onChange={e => setAptNm(e.target.value)}
               onKeyDown={e => e.key === 'Enter' && load(1)}
               placeholder="일부만 입력" style={{ ...inp, width: 130 }} />

        <AreaRangeInput arMin={arMin} arMax={arMax}
                        onMin={setArMin} onMax={setArMax} />

        <label style={lbl}>정렬</label>
        <select value={sort} onChange={e => setSort(e.target.value)} style={sel}>
          {SORTS.map(s => <option key={s.v} value={s.v}>{s.t}</option>)}
        </select>

        <button onClick={() => load(1)} style={btnP}>🔍 조회</button>
      </div>

      <div style={{ marginBottom: 8, fontSize: 13, color: '#475569' }}>
        총 <b>{total.toLocaleString()}</b>건
        {loading && <span style={{ marginLeft: 8, color: '#3b82f6' }}>조회 중…</span>}
      </div>

      {/* ── 결과 표 ── */}
      <div style={{ overflowX: 'auto', border: '1px solid #e2e8f0', borderRadius: 8 }}>
        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
          <thead>
            <tr style={{ background: '#f1f5f9' }}>
              {['거래일','법정동','단지명','전용면적','층','거래금액','평당가','유형','준공'].map(h => (
                <th key={h} style={th}>{h}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {rows.map(r => (
              <tr key={r.id} style={{ borderTop: '1px solid #f1f5f9' }}>
                <td style={td}>{r.deal_date}</td>
                <td style={td}>{r.umd_nm}</td>
                <td style={{ ...td, fontWeight: 600 }}>{r.apt_nm}</td>
                <td style={tdR}>
                  {Number(r.exclu_use_ar).toFixed(1)}㎡
                  <span style={{ color: '#94a3b8', marginLeft: 4 }}>
                    ({toPyeong(r.exclu_use_ar)}평)
                  </span>
                </td>
                <td style={tdR}>{r.floor_no}층</td>
                <td style={{ ...tdR, fontWeight: 600, color: '#1e40af' }}>
                  {fmtAmt(r.deal_amount)}
                </td>
                <td style={tdR}>{r.unit_price?.toLocaleString()}</td>
                <td style={td}>
                  <span style={{ fontSize: 11, padding: '2px 6px', borderRadius: 3,
                    background: r.dealing_gbn === '직거래' ? '#fef3c7' : '#dbeafe',
                    color: r.dealing_gbn === '직거래' ? '#92400e' : '#1e40af' }}>
                    {r.dealing_gbn || '-'}
                  </span>
                </td>
                <td style={tdR}>{r.build_year}</td>
              </tr>
            ))}
            {rows.length === 0 && !loading && (
              <tr><td colSpan={9} style={{ ...td, textAlign: 'center', padding: 30,
                    color: '#94a3b8' }}>조회 결과가 없습니다</td></tr>
            )}
          </tbody>
        </table>
      </div>

      {/* ── 페이징 ── */}
      {pages > 1 && (
        <div style={{ display: 'flex', gap: 4, justifyContent: 'center', marginTop: 12 }}>
          <button onClick={() => load(1)} disabled={page === 1} style={btnS}>≪</button>
          <button onClick={() => load(page - 1)} disabled={page === 1} style={btnS}>이전</button>
          <span style={{ padding: '6px 12px', fontSize: 13 }}>{page} / {pages}</span>
          <button onClick={() => load(page + 1)} disabled={page >= pages} style={btnS}>다음</button>
          <button onClick={() => load(pages)} disabled={page >= pages} style={btnS}>≫</button>
        </div>
      )}
    </div>
  );
}

const th  = { padding: '10px 8px', textAlign: 'left', fontWeight: 600,
              color: '#475569', whiteSpace: 'nowrap' };
const td  = { padding: '8px', whiteSpace: 'nowrap' };
const tdR = { ...td, textAlign: 'right' };
const btnP = { padding: '7px 16px', background: '#3b82f6', color: '#fff',
               border: 'none', borderRadius: 4, cursor: 'pointer', fontSize: 13 };
const btnS = { padding: '6px 12px', background: '#fff', border: '1px solid #cbd5e1',
               borderRadius: 4, cursor: 'pointer', fontSize: 13 };